// path: src/Components/TutorialDialog.js
import React, { useEffect, useMemo, useState } from "react";
import characters from "../Characters.json";
import {
    isShyCorgisUnlocked,
    isTwistedModeUnlocked,
} from "../utils/progression";

const BASIC_PICK_COUNT = 4;
const ADVANCED_CARDS_COUNT = 4;
const SHY_TUTORIAL_DEMO_INTERVAL_MS = 2000;

const getTutorialCards = (count) => characters.slice(0, count);

const getImgSrc = (card) => {
    const fileName = String(card.image).split("/").pop();
    return `${process.env.PUBLIC_URL}/images/${fileName}`;
};

const shuffleCards = (cards) => {
    const next = [...cards];

    for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
    }

    return next;
};

const TWISTED_ROTATIONS = [0, 90, 180, 270];

const getRandomTwistedRotation = () => {
    return TWISTED_ROTATIONS[
        Math.floor(Math.random() * TWISTED_ROTATIONS.length)
    ];
};

const rotateCards = (cards) => {
    return cards.map((card) => ({
        ...card,
        tutorialRotation: getRandomTwistedRotation(),
    }));
};

const EMOJI = {
    cyclone: "\u{1F300}",
    seeNoEvilMonkey: "\u{1F648}",
    checkMark: "\u2705",
    partyPopper: "\u{1F389}",
    trophy: "\u{1F3C6}",
    playingCard: "\u{1F0CF}",
    sparkles: "\u2728",
    directHit: "\u{1F3AF}",
    brain: "\u{1F9E0}",
};

const Emoji = ({ label, symbol }) => (
    <span role="img" aria-label={label}>
        {symbol}
    </span>
);

const TutorialDialog = ({
    open,
    tutorialType = "menu",
    onClose,
    onComplete,
}) => {
    const [activeTutorial, setActiveTutorial] = useState(tutorialType);
    const [cards, setCards] = useState(getTutorialCards(BASIC_PICK_COUNT));
    const [pickedIds, setPickedIds] = useState([]);
    const [message, setMessage] = useState("Pick any card to start.");
    const [warningCardId, setWarningCardId] = useState(null);
    const [shyDemoHiddenIds, setShyDemoHiddenIds] = useState([]);

    const twistedUnlocked = isTwistedModeUnlocked();
    const shyCorgisUnlocked = isShyCorgisUnlocked();

    const isBasic = activeTutorial === "basic";
    const isMenu = activeTutorial === "menu";
    const isAdvancedPrompt = activeTutorial === "advancedPrompt";
    const isTwisted = activeTutorial === "twisted";
    const isShy = activeTutorial === "shy";
    const isBlitz = activeTutorial === "blitz";

    const completed = pickedIds.length >= BASIC_PICK_COUNT;

    const pickedSet = useMemo(() => {
        return new Set(pickedIds.map(String));
    }, [pickedIds]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setActiveTutorial(tutorialType);
        resetTutorialState(tutorialType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tutorialType]);

    useEffect(() => {
        if (!open || activeTutorial !== "shy") {
            setShyDemoHiddenIds([]);
            return undefined;
        }

        const demoCards = cards.slice(0, 2);

        if (!demoCards.length) {
            return undefined;
        }

        const interval = setInterval(() => {
            setShyDemoHiddenIds((currentHiddenIds) => {
                if (currentHiddenIds.length > 0) {
                    return [];
                }

                return demoCards.map((card) => String(card.id));
            });
        }, SHY_TUTORIAL_DEMO_INTERVAL_MS);

        return () => {
            clearInterval(interval);
            setShyDemoHiddenIds([]);
        };
    }, [open, activeTutorial, cards]);

    if (!open) {
        return null;
    }

    function resetTutorialState(nextTutorial = activeTutorial) {
        const nextCards =
            nextTutorial === "twisted" || nextTutorial === "shy"
                ? rotateCards(getTutorialCards(ADVANCED_CARDS_COUNT))
                : getTutorialCards(BASIC_PICK_COUNT);

        setCards(nextCards);
        setPickedIds([]);
        setMessage("Pick any card to start.");
        setWarningCardId(null);
        setShyDemoHiddenIds([]);
    }

    const goToMenu = () => {
        setActiveTutorial("menu");
        resetTutorialState("menu");
    };

    const openTutorial = (nextTutorial) => {
        setActiveTutorial(nextTutorial);
        resetTutorialState(nextTutorial);
    };

    const handleClose = () => {
        resetTutorialState("menu");
        onClose();
    };

    const handleComplete = () => {
        resetTutorialState("menu");
        onComplete();
    };

    const handleBasicCardClick = (card) => {
        if (completed) {
            return;
        }

        const cardId = String(card.id);

        if (pickedSet.has(cardId)) {
            setWarningCardId(cardId);
            setMessage(
                "That was the same card again. In the real game, that ends your run. Pick a different card."
            );
            return;
        }

        const nextPickedIds = [...pickedIds, cardId];
        const nextPickedCount = nextPickedIds.length;

        setPickedIds(nextPickedIds);
        setWarningCardId(null);

        if (nextPickedCount === 1) {
            setMessage("Nice! The board shuffled. Now pick a different card.");
        } else if (nextPickedCount === 2) {
            setMessage("Great memory. Keep going — choose another new card.");
        } else if (nextPickedCount === 3) {
            setMessage("One more! Pick the last card you have not chosen yet.");
        } else {
            setMessage(
                "Congrats! You finished the tutorial."
            );
        }

        setCards((currentCards) => shuffleCards(currentCards));
    };

    const handleTwistedCardClick = (card) => {
        if (completed) {
            return;
        }

        const cardId = String(card.id);

        if (pickedSet.has(cardId)) {
            setWarningCardId(cardId);
            setMessage(
                "That was the same card again. In a real Twisted Mode run, that ends your run. Pick a different card."
            );
            return;
        }

        const nextPickedIds = [...pickedIds, cardId];
        const nextPickedCount = nextPickedIds.length;

        setPickedIds(nextPickedIds);
        setWarningCardId(null);

        if (nextPickedCount === 1) {
            setMessage(
                "Nice! The board shuffled, and the cards rotated. Pick a different card."
            );
        } else if (nextPickedCount === 2) {
            setMessage(
                "Good memory. Keep going — the same card may look different when rotated."
            );
        } else if (nextPickedCount === 3) {
            setMessage("One more! Pick the final card you have not chosen yet.");
        } else {
            setMessage(
                "Congrats! You completed a Twisted Mode tutorial."
            );
        }

        setCards((currentCards) => rotateCards(shuffleCards(currentCards)));
    };

    const renderAdvancedUnlockPrompt = () => (
        <>
            <div className="tutorialRuleBox">
                <strong>New modes unlocked!</strong>

                <div className="advancedTutorialText">
                    Twisted Mode and Shy Corgis are now available. You can view their
                    tutorials from the How to Play section before trying them in a run.
                </div>
            </div>

            <div className="tutorialTips">
                <div>
                    <Emoji label="Cyclone" symbol={EMOJI.cyclone} /> Twisted Mode rotates cards after shuffles.
                </div>
                <div>
                    <Emoji label="See no evil monkey" symbol={EMOJI.seeNoEvilMonkey} /> Shy Corgis makes cards temporarily flip face-down.
                </div>
                <div>
                    <Emoji label="Check mark" symbol={EMOJI.checkMark} /> Both modes can be toggled before choosing difficulty.
                </div>
            </div>

            <div className="tutorialActions">
                <button
                    type="button"
                    className="tutorialSecondaryButton"
                    onClick={handleClose}
                >
                    Later
                </button>

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={goToMenu}
                >
                    View Tutorials
                </button>
            </div>
        </>
    );

    const renderTutorialMenu = () => (
        <>
            <div className="tutorialRuleBox">
                Choose a tutorial. Basic teaches the main game. Advanced tutorials
                explain unlockable modes after you complete the base game.
            </div>

            <div className="tutorialMenu">
                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("basic")}
                >
                    <span className="tutorialMenuButton__title">
                        Basic Tutorial
                    </span>
                    <span className="tutorialMenuButton__description">
                        Learn the core rule: pick each card once while the board shuffles.
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("twisted")}
                    disabled={!twistedUnlocked}
                >
                    <span className="tutorialMenuButton__title">
                        Twisted Mode Tutorial
                    </span>
                    <span className="tutorialMenuButton__description">
                        Learn how rotated cards work.
                        {!twistedUnlocked && " Complete the base game to unlock."}
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("shy")}
                    disabled={!shyCorgisUnlocked}
                >
                    <span className="tutorialMenuButton__title">
                        Shy Corgis Tutorial
                    </span>
                    <span className="tutorialMenuButton__description">
                        Learn how cards temporarily flip face-down.
                        {!shyCorgisUnlocked && " Complete the base game to unlock."}
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("blitz")}
                >
                    <span className="tutorialMenuButton__title">
                        Blitz Tutorial
                    </span>
                    <span className="tutorialMenuButton__description">
                        Learn how the 2-minute timer and forced Hardcore rules work.
                    </span>
                </button>
            </div>

            <div className="tutorialActions">
                <button
                    type="button"
                    className="tutorialSecondaryButton"
                    onClick={handleClose}
                >
                    Close
                </button>
            </div>
        </>
    );

    const renderBasicTutorial = () => (
        <>
            <div className="tutorialRuleBox">
                Click each card only once. After every correct pick, the board
                shuffles. Picking the same card twice ends the run.
            </div>

            <div className="tutorialProgress">
                Practice picks: {pickedIds.length}/{BASIC_PICK_COUNT}
            </div>

            <div className="tutorialBoard" aria-label="Tutorial cards">
                {cards.map((card) => {
                    const cardId = String(card.id);
                    const warned = warningCardId === cardId;

                    return (
                        <button
                            type="button"
                            key={card.id}
                            className={`tutorialCard tutorialCard--image ${
                                warned ? "tutorialCard--warning" : ""
                            }`}
                            onClick={() => handleBasicCardClick(card)}
                        >
                            <img
                                src={getImgSrc(card)}
                                alt={card.name}
                                className="tutorialCardImage"
                                draggable="false"
                            />
                        </button>
                    );
                })}
            </div>

            <div
                className={`tutorialMessage ${
                    completed ? "tutorialMessage--success" : ""
                }`}
                role="status"
                aria-live="polite"
            >
                {message}
            </div>

            {completed && (
                <div className="tutorialCongrats">
                    <Emoji label="Party popper" symbol={EMOJI.partyPopper} /> You are ready for a real run!
                </div>
            )}

            <div className="tutorialTips">
                <div>
                    <Emoji label="Trophy" symbol={EMOJI.trophy} /> Clear levels to unlock harder modes.
                </div>
                <div>
                    <Emoji label="Playing card" symbol={EMOJI.playingCard} /> New cards you click are added to your collection.
                </div>
                <div>
                    <Emoji label="Sparkles" symbol={EMOJI.sparkles} /> Foil cards are rare special versions.
                </div>
            </div>

            <div className="tutorialActions">
                {tutorialType === "menu" && (
                    <button
                        type="button"
                        className="tutorialSecondaryButton"
                        onClick={goToMenu}
                    >
                        Back
                    </button>
                )}

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    Close Tutorial
                </button>
            </div>
        </>
    );

    const renderTwistedTutorial = () => (
        <>
            <div className="tutorialRuleBox">
                <strong>Twisted Mode rotates the cards.</strong>

                <div className="advancedTutorialText">
                    Play this practice round just like the real game. Pick each card
                    only once. After every correct pick, the board shuffles and each
                    card can rotate to 0°, 90°, 180°, or 270°.
                </div>
            </div>

            <div className="tutorialProgress">
                Twisted picks: {pickedIds.length}/{BASIC_PICK_COUNT}
            </div>

            <div className="tutorialBoard" aria-label="Twisted Mode tutorial cards">
                {cards.map((card) => {
                    const cardId = String(card.id);
                    const warned = warningCardId === cardId;

                    return (
                        <button
                            type="button"
                            key={card.id}
                            className={`tutorialCard tutorialCard--image ${
                                warned ? "tutorialCard--warning" : ""
                            }`}
                            onClick={() => handleTwistedCardClick(card)}
                        >
                            <img
                                src={getImgSrc(card)}
                                alt={card.name}
                                className="tutorialCardImage tutorialCardImage--advanced"
                                draggable="false"
                                style={{
                                    transform: `rotate(${card.tutorialRotation || 0}deg)`,
                                }}
                            />
                        </button>
                    );
                })}
            </div>

            <div
                className={`tutorialMessage ${
                    completed ? "tutorialMessage--success" : ""
                }`}
                role="status"
                aria-live="polite"
            >
                {message}
            </div>

            {completed && (
                <div className="tutorialCongrats">
                    <Emoji label="Cyclone" symbol={EMOJI.cyclone} /> You are ready for Twisted Mode!
                </div>
            )}

            <div className="tutorialTips">
                <div>
                    <Emoji label="Cyclone" symbol={EMOJI.cyclone} /> Rotations can change after every shuffle.
                </div>
                <div>
                    <Emoji label="Direct hit" symbol={EMOJI.directHit} /> All rotations are equally likely.
                </div>
                <div>
                    <Emoji label="Brain" symbol={EMOJI.brain} /> Remember the card, not just its direction.
                </div>
            </div>

            <div className="tutorialActions">
                <button
                    type="button"
                    className="tutorialSecondaryButton"
                    onClick={goToMenu}
                >
                    Back
                </button>

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    Close Tutorial
                </button>
            </div>
        </>
    );

    const renderShyTutorial = () => (
        <>
            <div className="tutorialRuleBox">
                <strong>Shy Corgis hide temporarily.</strong>

                <div className="advancedTutorialText">
                    In Shy Corgis, cards flip face-down for a few seconds, then flip
                    back over. Each card has its own timer, and no more than half the
                    board can hide at once.
                </div>
            </div>

            {/* <div className="tutorialProgress">
                Watch the cards flip like they do during a real Shy Corgis run.
            </div> */}

            <div
                className="tutorialBoard tutorialBoard--shyDemo"
                aria-label="Shy Corgis tutorial cards"
            >
                {cards.map((card) => {
                    const hiddenByShyDemo = shyDemoHiddenIds.includes(String(card.id));

                    return (
                        <div
                            key={card.id}
                            className={`tutorialCard tutorialCard--image tutorialCard--advanced ${
                                hiddenByShyDemo ? "tutorialCard--shyHidden" : ""
                            }`}
                        >
                            <div className="tutorialCardFlipInner">
                                <div className="tutorialCardBack" aria-hidden="true" />

                                <img
                                    src={getImgSrc(card)}
                                    alt={card.name}
                                    className="tutorialCardImage tutorialCardImage--advanced"
                                    draggable="false"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="tutorialMessage" role="status" aria-live="polite">
                Remember what each card was before it hides. When it flips back, keep
                playing without picking the same card twice.
            </div>

            <div className="tutorialTips">
                <div>
                    <Emoji label="See no evil monkey" symbol={EMOJI.seeNoEvilMonkey} /> Cards hide and return automatically.
                </div>
                <div>
                    <Emoji label="Check mark" symbol={EMOJI.checkMark} /> At least half the board stays face-up.
                </div>
                <div>
                    <Emoji label="Brain" symbol={EMOJI.brain} /> The challenge is remembering cards while some are hidden.
                </div>
            </div>

            <div className="tutorialActions">
                <button
                    type="button"
                    className="tutorialSecondaryButton"
                    onClick={goToMenu}
                >
                    Back
                </button>

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    Close Tutorial
                </button>
            </div>
        </>
    );

    const renderBlitzTutorial = () => (
        <>
            <div className="tutorialRuleBox">
                <strong>Blitz is a 2-minute challenge.</strong>

                <div className="advancedTutorialText">
                    Blitz always starts at Level 1 with 2 minutes on the clock.
                    Your goal is to see how far you can get before time runs out.
                </div>

                <div className="advancedTutorialText">
                    Hardcore is always active in Blitz, so picking the same card twice
                    immediately ends the run. Hearts cannot be used.
                </div>

                <div className="advancedTutorialText">
                    Twisted Mode and Shy Corgis can still be toggled before starting
                    Blitz if you want an extra challenge.
                </div>
            </div>

            <div className="tutorialActions">
                <button
                    type="button"
                    className="tutorialSecondaryButton"
                    onClick={goToMenu}
                >
                    Back
                </button>

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    Close Tutorial
                </button>
            </div>
        </>
    );

    return (
        <div className="tutorialOverlay" role="dialog" aria-modal="true">
            <div className="tutorialPanel">
                <div className="tutorialHeader">
                    <div>
                        <h2 className="impactFont tutorialTitle">
                            {isMenu && "How to Play"}
                            {isAdvancedPrompt && "New Tutorials Unlocked"}
                            {isBasic && "Basic Tutorial"}
                            {isTwisted && "Twisted Mode Tutorial"}
                            {isShy && "Shy Corgis Tutorial"}
                            {isBlitz && "Blitz Tutorial"}
                        </h2>

                        <div className="tutorialSubtitle">
                            {isMenu && "Choose what you want to learn."}
                            {isAdvancedPrompt && "Learn your newly unlocked modes."}
                            {isBasic && "Finish this 4-card practice round."}
                            {isTwisted && "Learn the rotated-card modifier."}
                            {isShy && "Learn the hiding-card modifier."}
                            {isBlitz && "Learn the timed Hardcore challenge."}
                        </div>
                    </div>

                    <button
                        type="button"
                        className="tutorialCloseButton"
                        onClick={handleClose}
                        aria-label="Close tutorial"
                    >
                        ×
                    </button>
                </div>

                {isMenu && renderTutorialMenu()}
                {isAdvancedPrompt && renderAdvancedUnlockPrompt()}
                {isBasic && renderBasicTutorial()}
                {isTwisted && renderTwistedTutorial()}
                {isShy && renderShyTutorial()}
                {isBlitz && renderBlitzTutorial()}
            </div>
        </div>
    );
};

export default TutorialDialog;